import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

import { BACKEND } from "../../lib/backend";

export default function PDFInspector({
  pdfPrefs,
  setPdfPrefs,
  setSlidesPerPage,
  setMarginPct,
  uploadBg,
}) {
  return (
    <div className="space-y-3 border-t border-zinc-800 pt-4">
      <div className="label-eyebrow">PDF settings</div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Header text</Label>
        <Input
          data-testid="pdf-header-text"
          value={pdfPrefs.pdf_header_text}
          onChange={(e) => setPdfPrefs({ ...pdfPrefs, pdf_header_text: e.target.value })}
          placeholder="e.g. {title}"
          className="bg-zinc-950 border-zinc-800 h-8 text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label className="label-eyebrow">Footer text</Label>
        <Input
          data-testid="pdf-footer-text"
          value={pdfPrefs.pdf_footer_text}
          onChange={(e) => setPdfPrefs({ ...pdfPrefs, pdf_footer_text: e.target.value })}
          placeholder="e.g. Page {page} of {total}"
          className="bg-zinc-950 border-zinc-800 h-8 text-sm"
        />
        <p className="text-[10px] text-zinc-500 font-mono">Tokens: {"{title}"}, {"{page}"}, {"{total}"}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="label-eyebrow">Default ads / page</Label>
          <Select
            value={String(pdfPrefs.pdf_slides_per_page)}
            onValueChange={(v) => {
              setPdfPrefs({ ...pdfPrefs, pdf_slides_per_page: Number(v) });
              setSlidesPerPage(Number(v));
            }}
          >
            <SelectTrigger data-testid="pdf-default-spp" className="bg-zinc-950 border-zinc-800 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="4">4</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="label-eyebrow">Default margin · {pdfPrefs.pdf_margin_pct}%</Label>
          <input
            data-testid="pdf-default-margin"
            type="range"
            min={0}
            max={15}
            step={1}
            value={pdfPrefs.pdf_margin_pct}
            onChange={(e) => {
              const v = Number(e.target.value);
              setPdfPrefs({ ...pdfPrefs, pdf_margin_pct: v });
              setMarginPct(v);
            }}
            className="w-full accent-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
        <div>
          <div className="text-sm">Cover page</div>
          <div className="text-[10px] text-zinc-500">Front-matter with title + subtitle</div>
        </div>
        <Switch
          data-testid="pdf-cover-toggle"
          checked={pdfPrefs.pdf_cover_enabled}
          onCheckedChange={(v) => setPdfPrefs({ ...pdfPrefs, pdf_cover_enabled: v })}
        />
      </div>
      {pdfPrefs.pdf_cover_enabled && (
        <div className="space-y-2 pl-3 border-l border-zinc-800">
          <Input
            data-testid="pdf-cover-subtitle"
            value={pdfPrefs.pdf_cover_subtitle}
            onChange={(e) => setPdfPrefs({ ...pdfPrefs, pdf_cover_subtitle: e.target.value })}
            placeholder="Subtitle (e.g. Spring 2026 Edition)"
            className="bg-zinc-950 border-zinc-800 h-8 text-sm"
          />
          <div className="flex items-center gap-2">
            {pdfPrefs.pdf_cover_image_url ? (
              <>
                <img
                  src={`${BACKEND}${pdfPrefs.pdf_cover_image_url}`}
                  alt=""
                  className="w-10 h-10 rounded object-cover border border-zinc-800"
                />
                <button
                  type="button"
                  data-testid="clear-cover-image"
                  onClick={() => setPdfPrefs({ ...pdfPrefs, pdf_cover_image_url: "" })}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              </>
            ) : (
              <Input
                data-testid="pdf-cover-image"
                type="file"
                accept="image/*"
                onChange={(e) => uploadBg("pdf_cover_image_url", e.target.files?.[0])}
                className="bg-zinc-950 border-zinc-800 text-xs"
              />
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
        <div>
          <div className="text-sm">Table of contents</div>
          <div className="text-[10px] text-zinc-500">Lists companies with page numbers</div>
        </div>
        <Switch
          data-testid="pdf-toc-toggle"
          checked={pdfPrefs.pdf_toc_enabled}
          onCheckedChange={(v) => setPdfPrefs({ ...pdfPrefs, pdf_toc_enabled: v })}
        />
      </div>

      <div className="space-y-1">
        <Label className="label-eyebrow">Content page numbering starts at</Label>
        <Input
          data-testid="pdf-page-number-start"
          type="number"
          min={1}
          step={1}
          value={pdfPrefs.pdf_page_number_start}
          onChange={(e) => setPdfPrefs({ ...pdfPrefs, pdf_page_number_start: Math.max(1, parseInt(e.target.value || "1", 10)) })}
          className="bg-zinc-950 border-zinc-800 h-8 text-sm"
        />
        <p className="text-[10px] text-zinc-500">Cover &amp; TOC are unnumbered. Content pages start here; {"{total}"} reports the content-page count.</p>
      </div>
    </div>
  );
}
